import { ActionActionTypeEnum, ActionTypeEnum, SystemEventTypeEnum, TWAction } from '@libs/types';
import React from 'react';
import { eventBus } from './EventBus';

const HEADER_CF_MITIGATED = 'cf-mitigated';
const CF_CHALLENGE = 'challenge';
const DEVICE_ID = 'deviceId';
const USER_LANG = 'user_lang';

export const getCookie = (name: string): string | undefined => {
    if (typeof document === 'undefined' || !document.cookie) {
        return undefined;
    }

    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }
    return undefined;
};

export const setCookie = (name: string, value: string, days?: number) => {
    if (typeof document === 'undefined') {
        return;
    }
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

export const getLocale = () => {
    return getCookie(USER_LANG) || navigator?.language;
};

export const setLocale = (locale: string) => {
    setCookie(USER_LANG, locale);
};

export const debounce = <TTimout extends React.MutableRefObject<NodeJS.Timeout | null>, TFunc extends (...args: Parameters<TFunc>) => void>(
    timeoutRef: TTimout,
    func: TFunc,
    waitFor: number,
) => {
    return (...arg: Parameters<TFunc>) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            func.apply(this, arg);
        }, waitFor);
    };
};

export const copyTextToClipboard = (text: string, toastMessage?: string) => {
    navigator.clipboard.writeText(text).then(
        () => {
            if (toastMessage) {
                eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, toastMessage);
            }
        },
        () => console.error('Failed to copy'),
    );
};

export const isCFForbidden = (response: Response) => {
    return response.headers.get(HEADER_CF_MITIGATED) === CF_CHALLENGE;
};

export const getHost = (isForWS?: boolean) => {
    const baseUrl = process.env.NX_PUBLIC_BASE_URL ?? '/';
    const orchestratorApi = process.env.NX_PUBLIC_ORCHESTRATOR_API ?? '';
    if (process.env.NX_PUBLIC_EMBEDDED_PLAYER) {
        const urlString = orchestratorApi + baseUrl;
        return isForWS ? urlString.replace('http', 'ws') : urlString;
    }
    return baseUrl;
};

export const camelCaseToKebabCase = (camelCaseString?: string | Array<string>) => {
    const processedString = Array.isArray(camelCaseString) ? camelCaseString.join(' ') : camelCaseString;

    return processedString ? processedString.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase() : undefined;
};
export const getFieldName = (autofill?: string | Array<string>, defaultName?: string) => {
    return Array.isArray(autofill) ? autofill[0] : (autofill ?? defaultName);
};
export const isProduction = () => process.env.NODE_ENV === 'production';

export const isLoadingAction = (action?: TWAction) =>
    !(action?.type === ActionTypeEnum.Player || action?.type === ActionTypeEnum.Widget || action?.actionType === ActionActionTypeEnum.onTimer);

export const getDeviceId = () => {
    let deviceId = localStorage?.getItem(DEVICE_ID);
    if (!deviceId) {
        deviceId = global.crypto.randomUUID();
        localStorage?.setItem(DEVICE_ID, deviceId);
    }
    return deviceId;
};
